import React, { createContext, useContext, useState, useEffect } from 'react';
import { theme as antdTheme } from 'antd';

const ThemeContext = createContext(null);

const THEME_STORAGE_KEY = 'hp-admin-theme';

/** Desktop sider width — keep App content offset in sync with LeftNavBar. */
export const SIDEBAR_WIDTH = 240;

/** Ant Design default-ish primary (v5). */
const ANTD_PRIMARY = '#1677ff';
const ANTD_PRIMARY_DARK = '#1668dc';

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * App-level tokens for unconverted pages (inline styles).
 * Shell + converted tools prefer ConfigProvider / antd components.
 */
export const lightTheme = {
  name: 'light',
  background: '#f5f5f5',
  backgroundSecondary: '#ffffff',
  text: 'rgba(0, 0, 0, 0.88)',
  textSecondary: 'rgba(0, 0, 0, 0.65)',
  textMuted: 'rgba(0, 0, 0, 0.45)',
  border: '#f0f0f0',
  borderLight: '#fafafa',
  sidebar: '#001529',
  sidebarHover: '#000c17',
  sidebarBorder: 'rgba(255, 255, 255, 0.08)',
  sidebarText: 'rgba(255, 255, 255, 0.85)',
  sidebarTextMuted: 'rgba(255, 255, 255, 0.45)',
  primary: ANTD_PRIMARY,
  primaryHover: ANTD_PRIMARY_DARK,
  danger: '#ff4d4f',
  dangerHover: '#ff7875',
  success: '#52c41a',
  warning: '#faad14',
  cardBackground: '#ffffff',
  cardShadow: 'rgba(0, 0, 0, 0.06)',
  inputBackground: '#ffffff',
  inputBorder: '#d9d9d9',
  tableHeader: '#fafafa',
  tableRowHover: '#fafafa',
  tableRowAlternate: '#fafafa',
};

export const darkTheme = {
  name: 'dark',
  background: '#141414',
  backgroundSecondary: '#1f1f1f',
  text: 'rgba(255, 255, 255, 0.85)',
  textSecondary: 'rgba(255, 255, 255, 0.65)',
  textMuted: 'rgba(255, 255, 255, 0.45)',
  border: '#303030',
  borderLight: '#1f1f1f',
  sidebar: '#141414',
  sidebarHover: '#1f1f1f',
  sidebarBorder: '#303030',
  sidebarText: 'rgba(255, 255, 255, 0.85)',
  sidebarTextMuted: 'rgba(255, 255, 255, 0.45)',
  primary: ANTD_PRIMARY_DARK,
  primaryHover: ANTD_PRIMARY,
  danger: '#ff4d4f',
  dangerHover: '#ff7875',
  success: '#49aa19',
  warning: '#d89614',
  cardBackground: '#1f1f1f',
  cardShadow: 'rgba(0, 0, 0, 0.45)',
  inputBackground: '#141414',
  inputBorder: '#434343',
  tableHeader: '#1f1f1f',
  tableRowHover: '#262626',
  tableRowAlternate: '#1a1a1a',
};

const ANTD_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji'";

/**
 * Lean ConfigProvider theme — prefer antd defaults; light bridge from ThemeContext.
 */
export const getAntdThemeConfig = (appTheme, isDarkMode) => ({
  algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: appTheme.primary,
    colorBgBase: appTheme.background,
    colorText: appTheme.text,
    colorTextSecondary: appTheme.textSecondary,
    colorBorder: appTheme.border,
    colorBgContainer: appTheme.cardBackground,
    colorError: appTheme.danger,
    colorSuccess: appTheme.success,
    colorWarning: appTheme.warning,
    borderRadius: 8,
    fontFamily: ANTD_FONT,
    controlHeight: 36,
  },
  components: {
    Layout: {
      siderBg: appTheme.sidebar,
      headerBg: appTheme.sidebar,
      bodyBg: appTheme.background,
      triggerBg: appTheme.sidebarHover,
    },
    Menu: {
      darkItemBg: 'transparent',
      darkSubMenuItemBg: 'transparent',
      itemBorderRadius: 8,
      itemMarginInline: 8,
      itemHeight: 40,
    },
    Card: {
      borderRadiusLG: 8,
    },
  },
});

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      return savedTheme === 'dark';
    } catch (error) {
      console.error('Error reading theme from localStorage:', error);
      return false;
    }
  });

  const theme = isDarkMode ? darkTheme : lightTheme;

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, isDarkMode ? 'dark' : 'light');
    } catch (error) {
      console.error('Error saving theme to localStorage:', error);
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  const value = {
    isDarkMode,
    theme,
    toggleTheme,
    antdThemeConfig: getAntdThemeConfig(theme, isDarkMode),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
